import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { MemberSelect } from '../ui/UserSelect';
import { listAgents } from '../ai/api';
import { ConditionEditor } from './ConditionEditor';
import type { ActionField, ActionSpec, Step, StepType, WaitSpec } from './api';
import type { Branch, Path, Problem } from './steps';

/**
 * The step list, nested one branch deep at a time.
 *
 * A list rather than a canvas. A canvas looks like the tool in the screenshots and is worse to
 * use: it needs a mouse, it does not fit a phone, it cannot be read by a screen reader, and the
 * thing people actually do — "add a wait of two days after this" — takes a drag instead of a
 * click. The order steps run in is the order they are written, which a list already says.
 */

const ADDABLE: StepType[] = ['ACTION', 'CONDITION', 'WAIT', 'STOP'];

export function StepList({
  steps,
  path,
  actions,
  fields,
  problems,
  onAdd,
  onChange,
  onRemove,
  onMove,
}: {
  steps: Step[];
  path: Path;
  actions: ActionSpec[];
  fields: readonly string[];
  problems: Problem[];
  onAdd: (into: Path, type: StepType) => void;
  onChange: (at: Path, change: (step: Step) => Step) => void;
  onRemove: (at: Path) => void;
  onMove: (at: Path, direction: -1 | 1) => void;
}): React.JSX.Element {
  return (
    <div className="steps">
      <ol className="steps__list">
        {steps.map((step, index) => {
          const here: Path = [...path, index];

          return (
            <li key={step.id} className="steps__item">
              <StepCard
                step={step}
                path={here}
                actions={actions}
                fields={fields}
                problems={problems}
                first={index === 0}
                last={index === steps.length - 1}
                onAdd={onAdd}
                onChange={onChange}
                onRemove={onRemove}
                onMove={onMove}
              />
            </li>
          );
        })}
      </ol>

      <div className="steps__add">
        {ADDABLE.map((type) => (
          <button
            key={type}
            type="button"
            className="button button--quiet button--compact"
            onClick={() => onAdd(path, type)}
          >
            {t(`automations.add.${type}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepCard({
  step,
  path,
  actions,
  fields,
  problems,
  first,
  last,
  onAdd,
  onChange,
  onRemove,
  onMove,
}: {
  step: Step;
  path: Path;
  actions: ActionSpec[];
  fields: readonly string[];
  problems: Problem[];
  first: boolean;
  last: boolean;
  onAdd: (into: Path, type: StepType) => void;
  onChange: (at: Path, change: (step: Step) => Step) => void;
  onRemove: (at: Path) => void;
  onMove: (at: Path, direction: -1 | 1) => void;
}): React.JSX.Element {
  const mine = problems.filter((problem) => problem.stepId === step.id);

  return (
    <div
      className={`step step--${step.type.toLowerCase()}${mine.length > 0 ? ' step--wrong' : ''}`}
    >
      <header className="step__head">
        <code className="backlog__key">{step.id}</code>
        <span className="strong small">{t(`automations.step.${step.type}`)}</span>

        <span className="step__tools">
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={first}
            aria-label={t('automations.moveUp')}
            onClick={() => onMove(path, -1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={last}
            aria-label={t('automations.moveDown')}
            onClick={() => onMove(path, 1)}
          >
            ↓
          </button>
          <button
            type="button"
            className="chip__remove"
            aria-label={t('automations.removeStep')}
            onClick={() => onRemove(path)}
          >
            ×
          </button>
        </span>
      </header>

      {step.type === 'ACTION' && (
        <ActionStep step={step} actions={actions} onChange={(change) => onChange(path, change)} />
      )}

      {step.type === 'WAIT' && (
        <WaitStep
          wait={step.wait ?? {}}
          onChange={(wait) => onChange(path, (s) => ({ ...s, wait }))}
        />
      )}

      {step.type === 'STOP' && <p className="muted small">{t('automations.stopHint')}</p>}

      {step.type === 'CONDITION' && (
        <div className="step__branches">
          <ConditionEditor
            value={step.conditions ?? { match: 'ALL', conditions: [] }}
            fields={fields}
            emptyHint={t('automations.noStepConditions')}
            onChange={(conditions) => onChange(path, (s) => ({ ...s, conditions }))}
          />

          {(['then', 'else'] as Branch[]).map((branch) => (
            <section key={branch} className="step__branch">
              <h4 className="record__subhead">{t(`automations.branch.${branch}`)}</h4>
              <StepList
                steps={step[branch] ?? []}
                path={[...path, branch]}
                actions={actions}
                fields={fields}
                problems={problems}
                onAdd={onAdd}
                onChange={onChange}
                onRemove={onRemove}
                onMove={onMove}
              />
            </section>
          ))}
        </div>
      )}

      {mine.length > 0 && (
        <ul className="step__problems">
          {mine.map((problem, index) => (
            <li key={index} className="small">
              {problem.code === 'missing_config'
                ? t('automations.problem.missing_config').replace('{field}', problem.detail ?? '')
                : t(`automations.problem.${problem.code}`)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ActionStep({
  step,
  actions,
  onChange,
}: {
  step: Step;
  actions: ActionSpec[];
  onChange: (change: (step: Step) => Step) => void;
}): React.JSX.Element {
  const spec = actions.find((action) => action.name === step.action);

  return (
    <div className="stack">
      <label className="stack__field">
        <span>{t('automations.action')}</span>
        <select
          className="select"
          value={step.action ?? ''}
          onChange={(event) =>
            // Switching action drops the old config: the keys of one action mean nothing to
            // another, and carrying them over would send the server fields it will not read.
            onChange((current) => ({ ...current, action: event.target.value, config: {} }))
          }
        >
          <option value="">{t('automations.pickAction')}</option>
          {actions.map((action) => (
            <option key={action.name} value={action.name}>
              {t(`automations.action.${action.name}`)}
            </option>
          ))}
        </select>
      </label>

      {spec?.fields.map((field) => (
        <ConfigField
          key={field.key}
          field={field}
          value={step.config?.[field.key]}
          onChange={(value) =>
            onChange((current) => ({
              ...current,
              config: { ...(current.config ?? {}), [field.key]: value },
            }))
          }
        />
      ))}
    </div>
  );
}

/** One input of an action, drawn the way its declared `kind` asks for. */
function ConfigField({
  field,
  value,
  onChange,
}: {
  field: ActionField;
  value: unknown;
  onChange: (value: unknown) => void;
}): React.JSX.Element {
  const label = (
    <span>
      {t(`automations.config.${field.key}`)}
      {field.required && <span className="required"> *</span>}
    </span>
  );

  const agents = useQuery({
    queryKey: ['agents'],
    queryFn: listAgents,
    enabled: field.kind === 'agent',
    staleTime: 5 * 60 * 1000,
  });

  if (field.kind === 'user') {
    return (
      <div className="stack__field">
        {label}
        <MemberSelect
          value={typeof value === 'string' ? value : null}
          placeholder={t('work.unassigned')}
          onChange={(next) => onChange(next)}
        />
      </div>
    );
  }

  if (field.kind === 'agent') {
    return (
      <label className="stack__field">
        {label}
        <select
          className="select"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">{t('automations.pickAgent')}</option>
          {/* Only a published agent can be run; a draft has no version to execute. */}
          {(agents.data?.agents ?? [])
            .filter((agent) => agent.status === 'ACTIVE' && agent.published_version !== null)
            .map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
        </select>
      </label>
    );
  }

  if (field.kind === 'select') {
    return (
      <label className="stack__field">
        {label}
        <select
          className="select"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value || undefined)}
        >
          <option value="">{t('automations.unset')}</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {optionLabel(option)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.kind === 'textarea') {
    return (
      <label className="stack__field">
        {label}
        <textarea
          rows={3}
          value={typeof value === 'string' ? value : ''}
          maxLength={field.maxLength ?? 5000}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    );
  }

  if (field.kind === 'number') {
    return (
      <label className="stack__field">
        {label}
        <input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(event) =>
            onChange(event.target.value === '' ? undefined : Number(event.target.value))
          }
        />
      </label>
    );
  }

  if (field.kind === 'color') {
    return (
      <label className="stack__field">
        {label}
        <input
          type="color"
          value={typeof value === 'string' && value !== '' ? value : '#64748b'}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    );
  }

  if (field.kind === 'json') {
    return (
      <label className="stack__field">
        {label}
        <textarea
          rows={3}
          className="mono"
          value={value === undefined ? '' : JSON.stringify(value, null, 2)}
          placeholder="{}"
          onChange={(event) => {
            // Kept as text until it parses: rejecting each keystroke would make it unusable.
            try {
              onChange(
                event.target.value.trim() === '' ? undefined : JSON.parse(event.target.value),
              );
            } catch {
              onChange(event.target.value);
            }
          }}
        />
      </label>
    );
  }

  return (
    <label className="stack__field">
      {label}
      <input
        value={typeof value === 'string' ? value : ''}
        maxLength={field.maxLength ?? 500}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

/**
 * The word for one option of a `select` field.
 *
 * The values the server offers are CRM record types and task priorities, and the product already
 * has a name for each. A third catalogue saying "Empresa" a third time is a third place to forget
 * when somebody renames it, so this reads the two that exist and falls back to the raw value.
 */
function optionLabel(value: string): string {
  for (const prefix of ['crm.entity.', 'work.priority.']) {
    const label = t(`${prefix}${value}`);

    if (label !== `${prefix}${value}`) return label;
  }

  return value;
}

/**
 * How long to hold.
 *
 * Delays are persisted and scheduled, never an in-memory timer (context.md section 86), so a wait
 * of two days survives a deploy. "Until business hours" is the one people ask for by name: nobody
 * wants an automation texting a customer at 3am.
 */
function WaitStep({
  wait,
  onChange,
}: {
  wait: WaitSpec;
  onChange: (wait: WaitSpec) => void;
}): React.JSX.Element {
  const unit: 'minutes' | 'hours' | 'days' =
    wait.days !== undefined ? 'days' : wait.minutes !== undefined ? 'minutes' : 'hours';
  const amount = wait[unit] ?? 1;

  return (
    <div className="cell-inline">
      <input
        type="number"
        min={1}
        max={365}
        className="points-input"
        value={amount}
        aria-label={t('automations.waitAmount')}
        onChange={(event) =>
          onChange({ ...pickBusinessHours(wait), [unit]: Number(event.target.value) })
        }
      />

      <select
        className="select select--compact"
        value={unit}
        aria-label={t('automations.waitUnit')}
        onChange={(event) => onChange({ ...pickBusinessHours(wait), [event.target.value]: amount })}
      >
        <option value="minutes">{t('automations.unit.minutes')}</option>
        <option value="hours">{t('automations.unit.hours')}</option>
        <option value="days">{t('automations.unit.days')}</option>
      </select>

      <label className="cell-inline small">
        <input
          type="checkbox"
          checked={wait.untilBusinessHours === true}
          onChange={(event) => onChange({ ...wait, untilBusinessHours: event.target.checked })}
        />
        <span className="muted">{t('automations.untilBusinessHours')}</span>
      </label>

      {wait.untilBusinessHours && (
        <span className="muted small">{t('automations.businessHoursHint')}</span>
      )}
    </div>
  );
}

/** Changing the unit replaces the delay rather than adding a second one. */
function pickBusinessHours(wait: WaitSpec): WaitSpec {
  return wait.untilBusinessHours ? { untilBusinessHours: true } : {};
}
